import { useIsRoomMember } from "./useIsRoomMember";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { isScreenshotSession } from "../../app/screenshotSession";
import { isRoomEligible } from "../utils/isRoomEligible";

/**
 * Whether to offer the Join affordance for `room` — the info-section button and
 * the sticky header button alike, which are one decision rendered twice.
 *
 * ONE definition, deliberately. This lived inline and identically in
 * RoomDiscoveryPage, RoomResourcesPage and RoomConferencesPage, and only the
 * first of the three carried the screenshot-session clause. That is how a
 * marketing capture of the Resources tab came back with a "Join" button on it:
 * not a missed page, a notion with three definitions. A fourth room page must
 * not be able to repeat it, so the rule is here and the pages take the answer.
 *
 * ⚠️ DO NOT DELETE THE `isScreenshotSession()` CLAUSE. It reads as redundant —
 * it is false for every real user, since it is set only after a valid minted
 * token authenticates through `/screenshot-auth`, so removing it changes nothing
 * a person would ever see. That is exactly why it is worth a warning: the only
 * caller it affects is the screenshot pipeline, and a capture that carries a
 * Join button goes into a CLIENT EMAIL, where it cannot be recalled.
 *
 * NOTHING PINS THE WIRING. Two tests in `useCanShowJoin.test.ts` cover the
 * clause, and deleting it here breaks all three pages at once. But a page that
 * builds its own predicate instead of calling this hook — which is the state
 * this file was extracted from — is caught by NO test and by NO type. Pinning it
 * would mean rendering all three pages in jsdom. If you are adding a fourth room
 * page with a Join affordance, call this hook; do not reimplement it.
 */
export function useCanShowJoin(
  roomId: string,
  // Only what the eligibility check reads, derived from it rather than restated:
  // a `PrivateRoom` here would be a second, wider claim about what this needs.
  room: Parameters<typeof isRoomEligible>[0]
): boolean {
  const { currentUser } = useCurrentUser();
  const hasJoinedRoom = useIsRoomMember(roomId);

  return (
    !hasJoinedRoom &&
    isRoomEligible(room, currentUser) &&
    !isScreenshotSession()
  );
}
